import api from './api.service';
import { LocalStorageService } from './local-storage.service';
import { STORAGE } from '../constants';

/**
 * Único punto de verdad para el ciclo de vida de la sesión.
 *
 * Contrato del backend (raptor-cpd, módulo auth):
 *  - access token JWT de 15 min (`expiresIn` segundos en la respuesta de login/refresh).
 *  - refresh token opaco de 8 h, ROTATIVO: cada `POST /auth/refresh` devuelve uno nuevo y el
 *    anterior queda "reemplazado". Si alguien vuelve a usar uno reemplazado, el backend REVOCA
 *    TODA LA FAMILIA (todas las sesiones de ese login). Por eso el refresh aquí es single-flight:
 *    si el interceptor HTTP, el WebSocket y el provider piden refresh al mismo tiempo, todos
 *    esperan la misma promesa y solo sale UNA petición.
 *
 * Eventos que emite en `window`:
 *  - `raptor:session-refreshed` (detail: { accessToken, expiresAt })
 *  - `raptor:session-expired` (detail: { reason })
 */

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  tokenType?: string;
  expiresIn?: number;
}

/** Refrescar cuando falten estos ms para que expire el access token. */
export const REFRESH_LEEWAY_MS = 60_000;

let inflight: Promise<string | null> | null = null;

function decodeJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export const SessionService = {
  getAccessToken(): string | null {
    return LocalStorageService.get(STORAGE.TOKEN) ?? null;
  },

  getSession(): any | null {
    return LocalStorageService.get(STORAGE.SESSION);
  },

  hasSession(): boolean {
    return !!this.getAccessToken();
  },

  /** Epoch ms en que expira el access token actual (del `exp` del JWT). */
  getAccessExpiry(): number | null {
    const t = this.getAccessToken();
    return t ? decodeJwtExp(t) : null;
  },

  /** ms que faltan para que expire el access token (negativo = ya expiró). */
  msUntilExpiry(): number | null {
    const exp = this.getAccessExpiry();
    return exp === null ? null : exp - Date.now();
  },

  /** Guarda el par devuelto por login o refresh (mezclando con la sesión actual: user, roles…). */
  saveTokens(pair: TokenPair & Record<string, any>): void {
    const current = LocalStorageService.get(STORAGE.SESSION) || {};
    LocalStorageService.save(STORAGE.SESSION, { ...current, ...pair });
    LocalStorageService.save(STORAGE.TOKEN, pair.accessToken);
    if (pair.user?.roles) LocalStorageService.save(STORAGE.ROLE_DATA, pair.user.roles);
    window.dispatchEvent(
      new CustomEvent('raptor:session-refreshed', {
        detail: { accessToken: pair.accessToken, expiresAt: decodeJwtExp(pair.accessToken) }
      })
    );
  },

  /**
   * Refresca el access token. Single-flight: llamadas concurrentes comparten la misma promesa.
   * Devuelve el nuevo access token, o null si no se pudo (y en ese caso ya cerró la sesión).
   */
  refreshAccessToken(): Promise<string | null> {
    if (inflight) return inflight;
    inflight = (async () => {
      const refreshToken = this.getSession()?.refreshToken;
      if (!refreshToken) {
        this.expire('sin refresh token');
        return null;
      }
      try {
        // Se usa `api` (axios crudo) y no HTTP para no entrar al interceptor de 401 → bucle.
        const res = await api.post('auth/refresh', { refreshToken });
        const pair: TokenPair | undefined = res?.data;
        if (!pair?.accessToken) {
          this.expire('refresh sin accessToken');
          return null;
        }
        this.saveTokens(pair);
        return pair.accessToken;
      } catch (err: any) {
        const status = err?.response?.status;
        // 401 = refresh inválido/expirado/reusado → sesión muerta. Otro error (red, 5xx): no
        // cerramos sesión; el access token puede seguir siendo válido y se reintenta después.
        if (status === 401 || status === 403) {
          this.expire('refresh rechazado por el backend');
        } else {
          console.warn('[session] refresh falló (transitorio):', err?.message ?? err);
        }
        return null;
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  },

  /** Cierra sesión: revoca el refresh en el backend (best-effort), limpia storage y va al login. */
  async logout(): Promise<void> {
    const refreshToken = this.getSession()?.refreshToken;
    try {
      if (refreshToken) await api.post('auth/logout', { refreshToken });
    } catch {
      /* best-effort */
    }
    this.expire('logout');
  },

  /** Limpia y redirige al login. Idempotente. */
  expire(reason: string): void {
    const wasLogged = this.hasSession();
    LocalStorageService.clear();
    window.dispatchEvent(new CustomEvent('raptor:session-expired', { detail: { reason } }));
    if (wasLogged && !window.location.pathname.includes('/auth/login')) {
      window.location.replace('/auth/login');
    }
  }
};
